import { Box, Button, Card, CardContent, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import React from 'react';
import { DayData } from '../../ContactInformation';

interface TimezoneSelectionSectionProps {
  days: DayData[]; // sliced days for current page
  onTimeSlotToggle: (dayIndex: number, slotIndex: number) => void;
  currentPage: number;
  onNextPage: () => void;
  onPrevPage: () => void;
  daysPerPage: number;
  totalDays: number;
}

export const TimezoneSelectionSection = ({
  days,
  onTimeSlotToggle,
  currentPage,
  onNextPage,
  onPrevPage,
  daysPerPage,
  totalDays,
}: TimezoneSelectionSectionProps): JSX.Element => {
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage >= Math.ceil(totalDays / daysPerPage) - 1;
  return (
    <Box width="100%" display="flex" flexDirection="column" alignItems="center" gap={2}>
      <Card sx={{ width: '100%', bgcolor: '#f9fbff', border: 'none', boxShadow: 'none' }}>
        <CardContent sx={{ pt: 2, pb: 4, px: '0' }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={'0px'}>
            {/* Left Arrow */}
            <IconButton
              onClick={onPrevPage}
              disabled={isFirstPage}
              data-testid="prev-page-button"
              sx={{ p: 0, opacity: isFirstPage ? 0.4 : 1 }}
            >
              <ChevronLeft fontSize="medium" />
            </IconButton>

            {/* Days Grid */}
            <Box display="flex" gap={'8px'} justifyContent="center" sx={{ overflowX: 'hidden' }}>
              {days.map((day, i) => {
                const realDayIndex = currentPage * daysPerPage + i;
                return (
                  <Box
                    key={realDayIndex}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    sx={{ width: 120 }}
                  >
                    {/* Day Header */}
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      textAlign="center"
                      py={1}
                    >
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: 12 }}>
                        {day.shortDay}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: 14 }}>
                        {day.date}
                      </Typography>
                    </Box>

                    {/* Time Slots */}
                    {day.timeSlots.map((slot, slotIndex) => (
                      <Button
                        key={`${realDayIndex}-${slotIndex}`}
                        variant="outlined"
                        size="small"
                        disabled={slot.disabled}
                        onClick={() => onTimeSlotToggle(realDayIndex, slotIndex)}
                        sx={{
                          height: 'auto',
                          padding: '4px 8px',
                          borderRadius: '50px',
                          whiteSpace: 'nowrap',
                          fontSize: 13,
                          fontWeight: '400',
                          textTransform: 'none',
                          color: slot.selected ? '#0f4bff' : '#262b2d',
                          borderColor: slot.selected ? '#0f4bff' : '#d4d5d5',
                          backgroundColor: slot.selected ? '#0f4bff4c' : 'transparent',
                          '&:hover': {
                            backgroundColor: slot.selected ? '#0f4bff33' : '#f1f1f1',
                          },
                        }}
                      >
                        {slot.time}
                      </Button>
                    ))}
                  </Box>
                );
              })}
            </Box>

            {/* Right Arrow */}
            <IconButton
              onClick={onNextPage}
              disabled={isLastPage}
              data-testid="next-page-button"
              sx={{ p: 0, opacity: isLastPage ? 0.4 : 1 }}
            >
              <ChevronRight fontSize="medium" />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
